import React, { useState } from 'react';
//
const API_BASE_URL = 'https://backend-qybe.onrender.com';

const resolveImageSrc = (image) => {
  if (!image) return null;

  // Format historique: image directement stockee comme URL string
  if (typeof image === 'string') {
    const value = image.trim().replace(/\\/g, '/');
    if (!value) return null;
    if (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('data:') ||
      value.startsWith('blob:')
    ) {
      return value;
    }
    if (value.startsWith('//')) {
      return `https:${value}`;
    }
    if (value.startsWith('/')) {
      return `${API_BASE_URL}${value}`;
    }
    return `${API_BASE_URL}/${value}`;
  }

  // Formats possibles apres update backend (Cloudinary, multer, etc.)
  if (typeof image === 'object') {
    const candidate = image.url || image.secure_url || image.imageUrl || image.src || image.path || image.location;
    return resolveImageSrc(candidate);
  }

  return null;
};

const ServiceImageGallery = ({ images = [], title = "", compact = false, className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Afficher jusqu'a 4 images pour respecter le quota des services.
  const displayImages = (Array.isArray(images) ? images : [images])
    .map(resolveImageSrc)
    .filter(Boolean)
    .slice(0, 4);
  
  if (!displayImages || displayImages.length === 0) {
    return (
      <div className={`relative overflow-hidden rounded-lg bg-gray-100 ${
        compact ? 'h-40 w-full' : 'h-80 w-full'
      } ${className}`}>
        <img
          src="/default-service.png"
          alt={title || "Image par défaut du service"}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className={`relative rounded-lg overflow-hidden bg-gray-100 group ${
      compact ? 'h-40' : 'h-80'
    } ${className}`}>
      {/* Image principale */}
      <img
        src={displayImages[currentIndex]}
        alt={`${title} - Slide ${currentIndex + 1}`}
        className="w-full h-full object-cover"
        onError={(e) => {
          if (e.currentTarget.dataset.fallbackApplied) return;
          e.currentTarget.dataset.fallbackApplied = 'true';
          e.currentTarget.src = '/default-service.png';
        }}
      />

      {/* Bouton précédent */}
      {displayImages.length > 1 && (
        <button
          onClick={goToPrevious}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
          aria-label="Image précédente"
        >
          ‹
        </button>
      )}

      {/* Bouton suivant */}
      {displayImages.length > 1 && (
        <button
          onClick={goToNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
          aria-label="Image suivante"
        >
          ›
        </button>
      )}

      {/* Indicateurs (dots) */}
      {displayImages.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
          {displayImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToImage(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Aller à l'image ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Compteur d'images */}
      {displayImages.length > 1 && (
        <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          {currentIndex + 1}/{displayImages.length}
        </div>
      )}
    </div>
  );
};

export default ServiceImageGallery;

